import React, { useEffect, useRef, useState, useCallback } from 'react';
import VijayImg from '../assets/VijayEngineer.png';
import NarayanImg from '../assets/NarayanEngineer.png';
import JuberImg from '../assets/JuberEngineer.png';
import RajImg from '../assets/RajEngineer.png';
import { API_BASE_URL } from '../constants';
import { Order } from '../services/orderService';
import { trackEvent, ClarityEvents } from '../utils/clarityTracking';

interface Engineer {
  id: number;
  name: string;
  photo: string;
  display: number;
  battery: number;
  stars: number;
  experience: string;
}

const ENGINEERS: Engineer[] = [
  { id: 16, name: 'Vijay',   photo: VijayImg,   display: 50,  battery: 400, stars: 4.8, experience: '4 yrs' },
  { id: 14, name: 'Narayan', photo: NarayanImg, display: 40,  battery: 257, stars: 4.7, experience: '3 yrs' },
  { id: 20, name: 'Juber',   photo: JuberImg,   display: 25,  battery: 146, stars: 4.6, experience: '2 yrs' },
  { id: 18, name: 'Raj',     photo: RajImg,     display: 23,  battery: 23,  stars: 4.5, experience: '1 yr'  },
];

// ── Donut Chart ─────────────────────────────────────────────────────────────

const DonutChart: React.FC<{ display: number; battery: number; animated: boolean }> = ({ display, battery, animated }) => {
  const size = 64;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const total = display + battery;
  const displayRatio = display / total;
  const batteryRatio = battery / total;
  const displayDash   = animated ? displayRatio * circ : 0;
  const batteryDash   = animated ? batteryRatio * circ : 0;
  const batteryOffset = displayRatio * circ;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f3f4f6" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#22c55e" strokeWidth={stroke}
          strokeDasharray={`${batteryDash} ${circ}`} strokeDashoffset={-batteryOffset} strokeLinecap="round"
          style={{ transition: animated ? 'stroke-dasharray 1s ease 0.15s' : 'none' }} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#3b82f6" strokeWidth={stroke}
          strokeDasharray={`${displayDash} ${circ}`} strokeDashoffset={0} strokeLinecap="round"
          style={{ transition: animated ? 'stroke-dasharray 1s ease 0s' : 'none' }} />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{total}+</span>
        <span style={{ fontSize: 8, color: '#9ca3af', lineHeight: 1.3 }}>repairs</span>
      </div>
    </div>
  );
};

// ── Star Row ─────────────────────────────────────────────────────────────────

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    {Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} width="11" height="11" viewBox="0 0 20 20"
        fill={i < Math.floor(rating) ? '#fbbf24' : (i === Math.floor(rating) && rating % 1 >= 0.5) ? '#fbbf24' : '#e5e7eb'}>
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
    <span style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginLeft: 3 }}>{rating}</span>
  </div>
);

// ── Status Badge ─────────────────────────────────────────────────────────────


// ── Portfolio Popup ───────────────────────────────────────────────────────────

const PortfolioPopup: React.FC<{ engineer: Engineer; onClose: () => void }> = ({ engineer, onClose }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetch(`${API_BASE_URL}/Technician/${engineer.id}/workload`, { signal: controller.signal })
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then((res: { entity: Order[]; isSuccess: boolean }) => {
        setOrders(Array.isArray(res.entity) ? res.entity : []);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setError('Could not load repairs. Please try again.');
        setLoading(false);
      });
    return () => controller.abort();
  }, [engineer.id]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-end',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxHeight: '88vh',
          background: '#fff', borderRadius: '20px 20px 0 0',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10 }}>
          <div style={{ width: 36, height: 4, background: '#e5e7eb', borderRadius: 2 }} />
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px 12px',
          borderBottom: '1px solid #f3f4f6',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', overflow: 'hidden',
            border: '2px solid #c0007a', flexShrink: 0,
          }}>
            <img src={engineer.photo} alt={engineer.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{engineer.name}'s Repair Portfolio</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Last 10 completed repairs</div>
          </div>
          <button onClick={onClose} style={{
            background: '#f3f4f6', border: 'none', borderRadius: '50%',
            width: 32, height: 32, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="14" height="14" fill="none" stroke="#6b7280" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '8px 16px 24px' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{
                  height: 72, borderRadius: 12, background: '#f9fafb',
                  animation: 'shimmerEng 1.4s infinite',
                }} />
              ))}
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#ef4444', fontSize: 14 }}>
              {error}
            </div>
          )}

          {!loading && !error && orders.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 14 }}>
              No repairs found.
            </div>
          )}

          {!loading && !error && orders.map((order, i) => (
            <div key={order.id} style={{
              padding: '12px 14px',
              marginBottom: i < orders.length - 1 ? 8 : 0,
              background: '#fafafa',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              {/* Device */}
              <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                {order.makeName} {order.modelName}
              </div>
              {/* Address */}
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, wordBreak: 'break-word' }}>
                {order.customerAddress || '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes shimmerEng {
          0%   { opacity: 0.4; }
          50%  { opacity: 0.8; }
          100% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

// ── Engineer Card (horizontal) ────────────────────────────────────────────────

const CARD_WIDTH = 288;

const EngineerCard: React.FC<{ engineer: Engineer }> = ({ engineer }) => {
  const [animated, setAnimated] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setAnimated(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div
        ref={ref}
        style={{
          width: CARD_WIDTH, minWidth: CARD_WIDTH, maxWidth: CARD_WIDTH,
          scrollSnapAlign: 'start', flexShrink: 0,
          background: '#fff', borderRadius: 16,
          border: '1px solid #f3f4f6',
          boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
          padding: 16, boxSizing: 'border-box' as const,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        {/* Top: photo + info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
            border: '2px solid #c0007a',
          }}>
            <img src={engineer.photo} alt={engineer.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>{engineer.name}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>
              Repair Technician · {engineer.experience}
            </div>
            <Stars rating={engineer.stars} />
          </div>
        </div>

        {/* Middle: donut + stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <DonutChart display={engineer.display} battery={engineer.battery} animated={animated} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: '#3b82f6', flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                {engineer.display}+ Display Replaced
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: '#22c55e', flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                {engineer.battery}+ Battery Replaced
              </span>
            </div>
          </div>
        </div>

        {/* Button */}
        <button
          onClick={() => {
            trackEvent(ClarityEvents.ENGINEER_PORTFOLIO_VIEW, { engineer: engineer.name, engineerId: engineer.id });
            setShowPopup(true);
          }}
          style={{
            width: '100%', padding: '9px 0',
            background: 'linear-gradient(90deg, #c0007a, #a8005a)',
            color: '#fff', border: 'none', borderRadius: 10,
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          View Portfolio
        </button>
      </div>

      {showPopup && (
        <PortfolioPopup engineer={engineer} onClose={() => setShowPopup(false)} />
      )}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const EngineerPortfolio: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollToIndex = useCallback((index: number) => {
    scrollRef.current?.scrollTo({ left: index * (CARD_WIDTH + 12), behavior: 'smooth' });
  }, []);

  const resetAutoScroll = useCallback(() => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % ENGINEERS.length;
        scrollToIndex(next);
        return next;
      });
    }, 5000);
  }, [scrollToIndex]);

  useEffect(() => {
    resetAutoScroll();
    return () => { if (autoScrollRef.current) clearInterval(autoScrollRef.current); };
  }, [resetAutoScroll]);

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeftRef.current = scrollRef.current?.scrollLeft ?? 0;
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    scrollRef.current.scrollLeft = scrollLeftRef.current - (e.pageX - scrollRef.current.offsetLeft - startX.current);
  };
  const handleMouseUp = () => { isDragging.current = false; resetAutoScroll(); };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ width: 4, height: 20, background: '#c0007a', borderRadius: 2 }} />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Our iPhone Experts</h3>
        <span style={{
          fontSize: 10, fontWeight: 700,
          background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
          color: '#fff', borderRadius: 20, padding: '2px 8px', letterSpacing: 0.4,
        }}>Our Engineers</span>
      </div>

      {/* Horizontal Slider */}
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          display: 'flex', gap: 12, overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
          cursor: 'grab', paddingBottom: 4,
        }}
      >
        {ENGINEERS.map(eng => <EngineerCard key={eng.id} engineer={eng} />)}
      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 10 }}>
        {ENGINEERS.map((_, i) => (
          <div
            key={i}
            onClick={() => { scrollToIndex(i); setActiveIndex(i); resetAutoScroll(); }}
            style={{
              width: i === activeIndex ? 18 : 6, height: 6, borderRadius: 3,
              background: i === activeIndex ? '#c0007a' : '#d1d5db',
              cursor: 'pointer', transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default EngineerPortfolio;
